import React from "react";
import PropTypes from "prop-types";
import {
    Avatar,
    Box,
    Button,
    Fade,
    IconButton,
    Popper,
    Typography,
    useMediaQuery,
} from "@mui/material";
import {
    Close,
    HowToRegOutlined,
    LogoutRounded,
    PersonAddAlt1,
    PersonOutlineRounded,
    ScheduleSendOutlined,
} from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import { colors } from "../../theme/colors";
import StatusDot from "../common/StatusDot";
import {
    getEloTier,
    getEloTierProgress,
} from "../common/eloTierUtils";
import {
    MembershipBadge,
    ProfileRankBadge,
    resolveMembershipDisplay,
} from "../profile/profileIdentityBadges";
import {
    isPendingRequestForRecipient,
    canReceiveFriendRequests,
} from "../../utils/friendRequests";
import useLobbyPlayerPreview, {
    formatKd,
    formatRating3,
    formatReputation,
    resolvePlayerSteamId,
} from "./useLobbyPlayerPreview";
import { MOBILE_NAV_MEDIA } from "../layout/layoutConstants";
import { useScrollLock } from "../common/lenisContext";
import BackdropOverlay from "../common/BackdropOverlay.jsx";

const CARD_WIDTH = "340px";
const CARD_PAD = "1.5rem";
const AVATAR_SIZE = "4rem";
const EMPTY_SLOT_FONT = "'JetBrains Mono', monospace";

const ACTION_CLIP = "polygon(6px 0, 100% 0, calc(100% - 6px) 100%, 0 100%)";

const PAPER_SX = {
    width: CARD_WIDTH,
    maxWidth: "calc(100vw - 1.5rem)",
    overflow: "visible",
    position: "relative",
    isolation: "isolate",
    boxSizing: "border-box",
    transformOrigin: "top left",
    borderRadius: "8px",
    border: "1px solid rgba(255,255,255,0.075)",
    background:
        "linear-gradient(165deg, rgba(18,19,23,0.995) 0%, rgba(11,12,15,0.997) 52%, rgba(8,9,11,1) 100%)",
    boxShadow:
        "inset 0 1px 0 rgba(255,255,255,0.055), inset 1px 0 0 rgba(255,255,255,0.018), inset 0 -1px 0 rgba(0,0,0,0.72), 0 24px 64px rgba(0,0,0,0.62), 0 8px 20px rgba(0,0,0,0.28)",
    backdropFilter: "blur(22px)",
    WebkitBackdropFilter: "blur(22px)",
    "&::before": {
        content: '""',
        position: "absolute",
        inset: 0,
        zIndex: -1,
        pointerEvents: "none",
        borderRadius: "inherit",
        background:
            "radial-gradient(ellipse 85% 42% at 22% -6%, rgba(232,163,61,0.075), transparent 66%), radial-gradient(ellipse 70% 55% at 100% 100%, rgba(40,48,66,0.11), transparent 70%)",
    },
    "&::after": {
        content: '""',
        position: "absolute",
        top: 0,
        left: "14%",
        right: "14%",
        height: "1px",
        zIndex: 4,
        pointerEvents: "none",
        background:
            "linear-gradient(90deg, transparent, rgba(232,163,61,.38), transparent)",
    },
};

const MOBILE_PAPER_SX = {
    ...PAPER_SX,
    width: "100%",
    maxWidth: "100%",
    maxHeight: "calc(100dvh - 2.5rem)",
    overflowY: "auto",
};

const CLOSE_BUTTON_SX = {
    position: "absolute",
    top: "0.8rem",
    right: "0.8rem",
    zIndex: 5,
    width: "1.9rem",
    height: "1.9rem",
    borderRadius: "0.55rem",
    color: colors.text.secondary,
    backgroundColor: "rgba(255, 255, 255, 0.04)",
    "&:hover": {
        backgroundColor: "rgba(255, 255, 255, 0.08)",
    },
};

const HEADER_SX = {
    position: "relative",
    height: "5rem",
    boxSizing: "border-box",
    overflow: "hidden",
    background:
        "radial-gradient(ellipse 220px 90px at 18% 0%, rgba(232,163,61,.1), transparent 72%), linear-gradient(135deg, rgba(27,24,20,.82) 0%, rgba(14,15,18,.97) 52%, rgba(9,10,12,.99) 100%)",
    "&::before": {
        content: '""',
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        background: `
            repeating-linear-gradient(
                118deg,
                transparent 0 10px,
                rgba(255, 236, 200, 0.028) 10px 11px
            )
        `,
        maskImage:
            "linear-gradient(180deg, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.45) 62%, transparent 100%)",
        WebkitMaskImage:
            "linear-gradient(180deg, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.45) 62%, transparent 100%)",
    },
};

const AVATAR_RING_SX = {
    position: "relative",
    zIndex: 2,
    width: AVATAR_SIZE,
    height: AVATAR_SIZE,
    flex: `0 0 ${AVATAR_SIZE}`,
    mt: "-2rem",
    p: "2px",
    boxSizing: "border-box",
    borderRadius: "50%",
    background:
        "linear-gradient(145deg, rgba(245,190,91,.88), rgba(171,108,28,.46) 48%, rgba(255,255,255,.10))",
    boxShadow:
        "0 6px 16px rgba(0,0,0,.42), 0 0 0 1px rgba(232,163,61,.08)",
};

const AVATAR_SX = {
    width: "100%",
    height: "100%",
    borderRadius: "50%",
    border: "1px solid " + colors.background.base,
    boxSizing: "border-box",
    backgroundColor: "rgba(255,255,255,0.055)",
};

function clippedActionSx(tone) {
    const isPrimary = tone === "primary";
    const isSuccess = tone === "success";
    const isPending = tone === "pending";

    return {
        width: "100%",
        minHeight: 36,
        px: isPending ? "0.55rem" : "0.7rem",
        justifyContent: "center",
        borderRadius: "8px",
        clipPath: ACTION_CLIP,
        textTransform: "none",
        whiteSpace: "nowrap",
        fontSize: isPending ? "0.68rem" : "0.73rem",
        fontWeight: isPending ? 600 : 700,
        letterSpacing: "0.03em",
        color: isPrimary
            ? colors.text.contrast
            : isSuccess
              ? alpha("#BBF7D0", 0.94)
              : alpha(colors.accent.soft, 0.88),
        border: isPrimary
            ? "1px solid rgba(246,199,107,0.4)"
            : isSuccess
              ? `1px solid ${alpha(colors.state.success.main, 0.22)}`
              : `1px solid ${alpha(colors.primary.main, 0.28)}`,
        background: isPrimary
            ? colors.gradient.accent
            : isSuccess
              ? `
                    linear-gradient(
                        180deg,
                        ${alpha(colors.state.success.main, 0.14)} 0%,
                        ${alpha(colors.state.success.main, 0.06)} 100%
                    )
                `
              : `
                    linear-gradient(
                        135deg,
                        rgba(232,163,61,0.12) 0%,
                        rgba(255,255,255,0.03) 55%,
                        rgba(0,0,0,0.2) 100%
                    )
                `,
        boxShadow: isPrimary
            ? "0 6px 16px rgba(232,163,61,0.16)"
            : isSuccess
              ? "inset 0 1px 0 rgba(255,255,255,0.04)"
              : "inset 0 1px 0 rgba(255,255,255,0.06), 0 4px 12px rgba(0,0,0,0.22)",
        backdropFilter: isPending ? "blur(8px)" : "none",
        transition:
            "background-color 160ms ease, border-color 160ms ease, color 160ms ease, box-shadow 160ms ease, filter 160ms ease",
        "& .MuiButton-startIcon": {
            mr: "0.35rem",
            color: isPending ? alpha(colors.accent.main, 0.9) : "inherit",
        },
        "&:hover": isPrimary
            ? {
                  filter: "brightness(1.04)",
                  boxShadow: "0 8px 18px rgba(232,163,61,0.22)",
              }
            : isSuccess
              ? {
                    background: `
                        linear-gradient(
                            180deg,
                            ${alpha(colors.state.success.main, 0.18)} 0%,
                            ${alpha(colors.state.success.main, 0.08)} 100%
                        )
                    `,
                    borderColor: alpha(colors.state.success.main, 0.3),
                }
              : undefined,
        "&.Mui-disabled": {
            opacity: 1,
            cursor: "default",
            color: isSuccess
                ? alpha("#BBF7D0", 0.94)
                : alpha(colors.accent.soft, 0.88),
            background: isSuccess
                ? `
                    linear-gradient(
                        180deg,
                        ${alpha(colors.state.success.main, 0.14)} 0%,
                        ${alpha(colors.state.success.main, 0.06)} 100%
                    )
                `
                : `
                    linear-gradient(
                        135deg,
                        rgba(232,163,61,0.12) 0%,
                        rgba(255,255,255,0.03) 55%,
                        rgba(0,0,0,0.2) 100%
                    )
                `,
            borderColor: isSuccess
                ? alpha(colors.state.success.main, 0.22)
                : alpha(colors.primary.main, 0.28),
            boxShadow: isSuccess
                ? "inset 0 1px 0 rgba(255,255,255,0.04)"
                : "inset 0 1px 0 rgba(255,255,255,0.06), 0 4px 12px rgba(0,0,0,0.22)",
            "& .MuiButton-startIcon": {
                color: isPending ? alpha(colors.accent.main, 0.9) : "inherit",
            },
        },
    };
}

const VIEW_PROFILE_SX = {
    minWidth: 0,
    width: "100%",
    minHeight: 36,
    px: "0.72rem",
    borderRadius: "7px",
    justifyContent: "center",
    textTransform: "none",
    letterSpacing: "0.005em",
    fontSize: "0.72rem",
    fontWeight: 600,
    color: alpha("#fff", 0.62),
    border: "1px solid rgba(255,255,255,0.1)",
    background: `
        linear-gradient(
            180deg,
            rgba(255,255,255,0.045) 0%,
            rgba(255,255,255,0.02) 100%
        )
    `,
    boxShadow:
        "inset 0 1px 0 rgba(255,255,255,0.05), 0 4px 12px rgba(0,0,0,0.18)",
    transition:
        "background-color 160ms ease, border-color 160ms ease, color 160ms ease, box-shadow 160ms ease, filter 160ms ease",
    "& .MuiButton-startIcon": {
        mr: "0.4rem",
        color: "inherit",
    },
    "&:hover": {
        backgroundColor: "rgba(255,255,255,0.06)",
        borderColor: "rgba(255,255,255,0.14)",
        color: colors.text.primary,
    },
};

const KICK_ACTION_SX = {
    minWidth: 0,
    width: "100%",
    minHeight: "2.15rem",
    px: "0.75rem",
    justifyContent: "center",
    borderRadius: "7px",
    textTransform: "none",
    letterSpacing: "0.005em",
    fontSize: "0.72rem",
    fontWeight: 600,
    color: alpha(colors.text.secondary, 0.8),
    border: "1px solid transparent",
    background: "transparent",
    boxShadow: "none",
    transition: "color 160ms ease, background 160ms ease",
    "& .MuiButton-startIcon": {
        mr: "0.45rem",
        color: "inherit",
    },
    "&:hover": {
        color: colors.state.danger.main,
        background: alpha(colors.state.danger.hover, 0.14),
        backgroundColor: alpha(colors.state.danger.hover, 0.14),
        borderColor: "transparent",
        boxShadow: "none",
    },
    "&:active": {
        background: alpha(colors.state.danger.hover, 0.18),
        backgroundColor: alpha(colors.state.danger.hover, 0.18),
    },
};

const ACTIONS_FOOTER_SX = {
    mt: "1.1rem",
    pt: "0.5rem",
    borderTop: "1px solid rgba(255,255,255,0.065)",
};

function resolveStatus(isOnline, playerStatus) {
    if (!isOnline) return { label: "Offline", live: false };
    if (playerStatus === "inMatch") return { label: "In match", live: true };
    if (playerStatus === "inMatchMaking") {
        return { label: "In queue", live: true };
    }
    return { label: "Online", live: true };
}

function StatCell({ label, value, divider = false }) {
    return (
        <Box
            sx={{
                minWidth: 0,
                flex: "1 1 0",
                textAlign: "center",
                px: "0.25rem",
                borderLeft: divider
                    ? "1px solid rgba(255,255,255,.045)"
                    : "none",
            }}
        >
            <Typography
                component="p"
                sx={{
                    m: 0,
                    fontFamily: EMPTY_SLOT_FONT,
                    fontSize: "0.56rem",
                    fontWeight: 500,
                    letterSpacing: "0.08em",
                    color: alpha(colors.text.secondary, 0.52),
                    lineHeight: 1,
                    textTransform: "uppercase",
                }}
            >
                {label}
            </Typography>
            <Typography
                component="p"
                sx={{
                    m: 0,
                    mt: "0.34rem",
                    fontFamily: EMPTY_SLOT_FONT,
                    fontSize: "1.08rem",
                    fontWeight: 600,
                    color: colors.text.primary,
                    letterSpacing: "0.06em",
                    lineHeight: 1,
                    fontVariantNumeric: "tabular-nums",
                }}
            >
                {value}
            </Typography>
        </Box>
    );
}

StatCell.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    divider: PropTypes.bool,
};

function RankEloStrip({ elo }) {
    const tier = getEloTier(elo);
    const { nextTier, isMax } = getEloTierProgress(elo);
    const hasElo = elo != null && Number.isFinite(Number(elo));
    const accent = tier.border;

    return (
        <Box
            sx={{
                position: "relative",
                mt: "0.95rem",
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "0.75rem",
                overflow: "visible",
                px: "0.85rem",
                py: "0.72rem",
                borderRadius: 0,
                border: "none",
                background: `
                    linear-gradient(
                        90deg,
                        ${alpha(accent, 0.14)} 0%,
                        ${alpha(accent, 0.045)} 42%,
                        ${alpha(accent, 0.018)} 100%
                    )
                `,
                "&::before": {
                    content: '""',
                    position: "absolute",
                    left: "5px",
                    top: "20%",
                    bottom: "20%",
                    width: "2px",
                    borderRadius: "999px",
                    background: `linear-gradient(180deg, transparent, ${accent}, transparent)`,
                    boxShadow: `0 0 9px ${alpha(accent, 0.32)}`,
                    pointerEvents: "none",
                },
            }}
        >
            <Box sx={{ minWidth: 0 }}>
                <Typography
                    component="p"
                    sx={{
                        m: 0,
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        letterSpacing: "0.04em",
                        lineHeight: 1.15,
                        color: alpha(tier.text, 0.92),
                    }}
                >
                    {tier.label}
                </Typography>
                <Box
                    sx={{
                        mt: "0.28rem",
                        display: "flex",
                        alignItems: "baseline",
                        gap: "0.32rem",
                    }}
                >
                    <Typography
                        component="span"
                        sx={{
                            fontFamily: EMPTY_SLOT_FONT,
                            fontSize: "0.98rem",
                            fontWeight: 600,
                            letterSpacing: "0.02em",
                            lineHeight: 1,
                            color: colors.text.primary,
                            fontVariantNumeric: "tabular-nums",
                        }}
                    >
                        {hasElo
                            ? Number(elo).toLocaleString("en-US")
                            : "—"}
                    </Typography>
                    <Typography
                        component="span"
                        sx={{
                            fontSize: "0.58rem",
                            fontWeight: 650,
                            letterSpacing: "0.1em",
                            textTransform: "uppercase",
                            color: alpha(colors.text.secondary, 0.52),
                            lineHeight: 1,
                        }}
                    >
                        ELO
                    </Typography>
                </Box>
            </Box>

            <Box sx={{ flex: "0 0 auto", textAlign: "right" }}>
                {isMax ? (
                    <Typography
                        component="p"
                        sx={{
                            m: 0,
                            fontFamily: "'Orbitron', sans-serif",
                            fontSize: "0.62rem",
                            fontWeight: 800,
                            letterSpacing: "0.14em",
                            textTransform: "uppercase",
                            lineHeight: 1.15,
                            background:
                                "linear-gradient(180deg, #FFF6D2 0%, #F6D67A 32%, #E8A33D 68%, #B8741F 100%)",
                            backgroundClip: "text",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            filter: `
                                drop-shadow(0 0 8px rgba(232,163,61,0.42))
                                drop-shadow(0 1px 0 rgba(80,42,0,0.55))
                            `,
                        }}
                    >
                        Max Rank
                    </Typography>
                ) : (
                    <>
                        <Typography
                            component="p"
                            sx={{
                                m: 0,
                                fontSize: "0.52rem",
                                fontWeight: 650,
                                letterSpacing: "0.1em",
                                textTransform: "uppercase",
                                color: alpha(colors.text.secondary, 0.52),
                                lineHeight: 1,
                            }}
                        >
                            Next rank
                        </Typography>
                        <Typography
                            component="p"
                            sx={{
                                m: 0,
                                mt: "0.28rem",
                                fontSize: "0.78rem",
                                fontWeight: 700,
                                letterSpacing: "0.02em",
                                lineHeight: 1,
                                color: colors.text.primary,
                            }}
                        >
                            {nextTier?.label || "—"}
                        </Typography>
                    </>
                )}
            </Box>
        </Box>
    );
}

RankEloStrip.propTypes = {
    elo: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

function FriendAction({
    isFriend,
    hasPendingRequest,
    canAddFriend,
    onAddFriend,
}) {
    if (isFriend) {
        return (
            <Button
                type="button"
                disabled
                disableElevation
                aria-label="Already friends"
                startIcon={
                    <HowToRegOutlined sx={{ fontSize: "0.95rem !important" }} />
                }
                sx={clippedActionSx("success")}
            >
                Friends
            </Button>
        );
    }

    if (hasPendingRequest) {
        return (
            <Button
                type="button"
                disabled
                disableElevation
                aria-label="Friend request sent"
                startIcon={
                    <ScheduleSendOutlined
                        sx={{ fontSize: "0.9rem !important" }}
                    />
                }
                sx={clippedActionSx("pending")}
            >
                Request sent
            </Button>
        );
    }

    if (!canAddFriend) return null;

    return (
        <Button
            type="button"
            disableElevation
            onClick={onAddFriend}
            startIcon={
                <PersonAddAlt1 sx={{ fontSize: "0.95rem !important" }} />
            }
            sx={clippedActionSx("primary")}
        >
            Add friend
        </Button>
    );
}

FriendAction.propTypes = {
    isFriend: PropTypes.bool,
    hasPendingRequest: PropTypes.bool,
    canAddFriend: PropTypes.bool,
    onAddFriend: PropTypes.func,
};

export default function LobbyPlayerPreviewCard({
    open,
    anchorEl,
    player,
    avatarSrc,
    isOnline,
    playerStatus,
    isFriend,
    hasPendingRequest,
    pendingRequests = [],
    canAddFriend,
    canKick,
    onProfile,
    onKick,
    onAddFriend,
    onClose,
}) {
    const isMobile = useMediaQuery(MOBILE_NAV_MEDIA);
    useScrollLock(Boolean(isMobile && open));
    const preview = useLobbyPlayerPreview(player, open);
    const nickname = player?.nickname || "Player";
    const status = resolveStatus(isOnline, playerStatus);
    const targetSteamId = preview.targetSteamId || resolvePlayerSteamId(player);
    const effectiveHasPendingRequest =
        hasPendingRequest ||
        (Boolean(targetSteamId) &&
            pendingRequests.some((request) =>
                isPendingRequestForRecipient(request, targetSteamId),
            ));
    const canSendFriendRequest =
        Boolean(canAddFriend) &&
        canReceiveFriendRequests({
            accountStatus:
                preview.accountStatus || player?.accountStatus,
        });
    const showFriendAction =
        isFriend || effectiveHasPendingRequest || canSendFriendRequest;
    const membershipDisplay = resolveMembershipDisplay(player, {
        compact: true,
    });

    const card = (
                    <Box
                        data-lobby-player-menu
                        role="dialog"
                        aria-label={nickname + " player menu"}
                        sx={isMobile ? MOBILE_PAPER_SX : PAPER_SX}
                    >
                        {isMobile ? (
                            <IconButton
                                onClick={onClose}
                                aria-label="Close dialog"
                                sx={CLOSE_BUTTON_SX}
                            >
                                <Close sx={{ fontSize: "0.95rem" }} />
                            </IconButton>
                        ) : null}
                        <Box
                            sx={{
                                overflow: "hidden",
                                borderRadius: "inherit",
                            }}
                        >
                        <Box sx={HEADER_SX}>
                            {!isMobile && preview.globalRank != null ? (
                                <Box
                                    sx={{
                                        position: "absolute",
                                        top: "0.65rem",
                                        right: CARD_PAD,
                                        zIndex: 2,
                                    }}
                                >
                                    <ProfileRankBadge
                                        rank={preview.globalRank}
                                        size="compact"
                                    />
                                </Box>
                            ) : null}
                        </Box>

                        <Box
                            sx={{
                                px: CARD_PAD,
                                pt: 0,
                                pb: CARD_PAD,
                            }}
                        >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: isMobile
                                    ? "space-between"
                                    : "flex-start",
                                width: isMobile ? "100%" : "auto",
                                ...(isMobile ? { mt: "-2rem" } : {}),
                            }}
                        >
                        <Box
                            sx={{
                                ...AVATAR_RING_SX,
                                ...(isMobile ? { mt: 0 } : {}),
                            }}
                        >
                            <Avatar
                                src={avatarSrc || undefined}
                                alt=""
                                sx={AVATAR_SX}
                            />
                            {player?.flagImageUrl ? (
                                <Box
                                    component="img"
                                    src={player.flagImageUrl}
                                    alt=""
                                    aria-hidden
                                    sx={{
                                        position: "absolute",
                                        right: "-1px",
                                        bottom: "-1px",
                                        width: "0.92rem",
                                        height: "0.92rem",
                                        borderRadius: "50%",
                                        border: "2px solid rgba(11,12,15,1)",
                                        objectFit: "cover",
                                    }}
                                />
                            ) : null}
                        </Box>
                        {isMobile && preview.globalRank != null ? (
                            <ProfileRankBadge
                                rank={preview.globalRank}
                                size="compact"
                            />
                        ) : null}
                        </Box>

                        <Box
                            sx={{
                                mt: "0.85rem",
                                display: "flex",
                                alignItems: "center",
                                gap: "0.5rem",
                                minWidth: 0,
                            }}
                        >
                            <Typography
                                component="p"
                                title={nickname}
                                sx={{
                                    m: 0,
                                    minWidth: 0,
                                    color: colors.text.primary,
                                    fontSize: "1.05rem",
                                    fontWeight: 700,
                                    letterSpacing: "0.005em",
                                    lineHeight: 1.15,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {nickname}
                            </Typography>
                            {membershipDisplay ? (
                                <MembershipBadge
                                    label={membershipDisplay.label}
                                    size="compact"
                                />
                            ) : null}
                        </Box>

                        <Box
                            sx={{
                                mt: "0.45rem",
                                display: "flex",
                                alignItems: "center",
                                gap: "0.38rem",
                            }}
                        >
                            <StatusDot
                                online={status.live}
                                sx={{ fontSize: "0.44rem" }}
                            />
                            <Typography
                                component="span"
                                sx={{
                                    color: status.live
                                        ? alpha(
                                              colors.state.success.main,
                                              0.86,
                                          )
                                        : alpha(colors.text.secondary, 0.58),
                                    fontSize: "0.62rem",
                                    fontWeight: 650,
                                    letterSpacing: "0.06em",
                                    lineHeight: 1,
                                }}
                            >
                                {status.label}
                            </Typography>
                        </Box>

                        <RankEloStrip elo={preview.elo} />

                        <Box
                            sx={{
                                mt: "1.2rem",
                                width: "100%",
                                display: "flex",
                                alignItems: "center",
                                px: 0,
                                py: "0.35rem",
                            }}
                        >
                            <StatCell
                                label="K/D"
                                value={formatKd(preview.kdRatio)}
                            />
                            <StatCell
                                label="R3"
                                value={formatRating3(preview.rating3)}
                                divider
                            />
                            <StatCell
                                label="Reputation"
                                value={formatReputation(preview.reputation)}
                                divider
                            />
                        </Box>

                        <Box
                            sx={{
                                mt: "1.85rem",
                                display: "flex",
                                flexDirection: "column",
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "0.45rem",
                                }}
                            >
                                {showFriendAction ? (
                                    <Box sx={{ flex: "1 1 0", minWidth: 0 }}>
                                        <FriendAction
                                            isFriend={isFriend}
                                            hasPendingRequest={
                                                effectiveHasPendingRequest
                                            }
                                            canAddFriend={
                                                canSendFriendRequest
                                            }
                                            onAddFriend={() =>
                                                onAddFriend(targetSteamId)
                                            }
                                        />
                                    </Box>
                                ) : null}

                                <Box
                                    sx={{
                                        flex: showFriendAction
                                            ? "1 1 0"
                                            : "1 1 auto",
                                        minWidth: 0,
                                    }}
                                >
                                    <Button
                                        type="button"
                                        disableElevation
                                        disableRipple
                                        onClick={onProfile}
                                        startIcon={
                                            <PersonOutlineRounded
                                                sx={{
                                                    fontSize:
                                                        "0.95rem !important",
                                                }}
                                            />
                                        }
                                        sx={VIEW_PROFILE_SX}
                                    >
                                        View profile
                                    </Button>
                                </Box>
                            </Box>

                            {canKick ? (
                                <Box sx={ACTIONS_FOOTER_SX}>
                                    <Button
                                        type="button"
                                        disableElevation
                                        disableRipple
                                        onClick={onKick}
                                        startIcon={
                                            <LogoutRounded
                                                sx={{
                                                    fontSize:
                                                        "0.95rem !important",
                                                }}
                                            />
                                        }
                                        sx={KICK_ACTION_SX}
                                    >
                                        Kick from lobby
                                    </Button>
                                </Box>
                            ) : null}
                        </Box>
                        </Box>
                        </Box>
                    </Box>
    );

    if (isMobile) {
        return (
            <BackdropOverlay
                open={open}
                onClick={onClose}
                contentSx={{ width: "100%", maxWidth: "100%" }}
            >
                {card}
            </BackdropOverlay>
        );
    }

    return (
        <Popper
            open={open}
            anchorEl={anchorEl}
            placement="right-start"
            modifiers={[
                { name: "offset", options: { offset: [0, 12] } },
                {
                    name: "preventOverflow",
                    options: { padding: 12, altAxis: true, tether: true },
                },
                {
                    name: "flip",
                    options: {
                        fallbackPlacements: [
                            "left-start",
                            "right-end",
                            "left-end",
                            "bottom-start",
                        ],
                        padding: 12,
                    },
                },
            ]}
            sx={{ zIndex: 1400 }}
            transition
        >
            {({ TransitionProps }) => (
                <Fade {...TransitionProps} timeout={180}>
                    {card}
                </Fade>
            )}
        </Popper>
    );
}

LobbyPlayerPreviewCard.propTypes = {
    open: PropTypes.bool,
    anchorEl: PropTypes.object,
    player: PropTypes.object,
    avatarSrc: PropTypes.string,
    isOnline: PropTypes.bool,
    playerStatus: PropTypes.string,
    isFriend: PropTypes.bool,
    hasPendingRequest: PropTypes.bool,
    pendingRequests: PropTypes.array,
    canAddFriend: PropTypes.bool,
    canKick: PropTypes.bool,
    onProfile: PropTypes.func,
    onKick: PropTypes.func,
    onAddFriend: PropTypes.func,
    onClose: PropTypes.func,
};
